import { SimulationBubble } from '@/components/public/Visualisations/BubblesV2/BubblesSimulation'
import {
  BubbleHaloMetadata,
  BubblePulseMetadata,
} from '@/components/public/Visualisations/BubblesV2/Metadata'
import { makeDarker } from '@/helpers'
import typedStore from '@/store/typedStore'
import { Coordinate2D, HollowBubbleDisplays, VisNode } from '@/types'
import { hsl } from 'd3-color'

const DEFAULT_COLOUR = 'red'
const DEFAULT_STROKE_COLOUR = 'black'
const HOLLOW_FILL_COLOUR = 'rgba(0, 0, 0, 0)' // transparent

export class BubbleMetadata implements SimulationBubble {
  protected _visNode: VisNode
  private _haloMetadata: BubbleHaloMetadata
  private _pulseMetadata: BubblePulseMetadata
  private _onPositionChanged: (coordinates: Coordinate2D) => void = () => {}

  private _x: number
  private _y: number
  destinationPosition: Coordinate2D

  constructor(visNode: VisNode, initialPosition: Coordinate2D) {
    this._visNode = visNode
    this._haloMetadata = new BubbleHaloMetadata(this)
    this._pulseMetadata = new BubblePulseMetadata(this)
    const { x, y } = initialPosition
    this._x = x
    this._y = y
    this.destinationPosition = initialPosition
  }

  get path() {
    return this._visNode.path
  }

  get x(): number {
    return this._x
  }

  set x(x: number) {
    this._x = x
    this._onPositionChanged({ x, y: this.y })
  }

  get y(): number {
    return this._y
  }

  set y(y: number) {
    this._y = y
    this._onPositionChanged({ x: this.x, y })
  }

  get currentPosition(): Coordinate2D {
    return { x: this._x, y: this._y }
  }

  get level1(): string {
    return this._visNode.path.level1
  }

  // The total Bubble radius used by the simulation.
  // This includes the Bubble radius, the total halo width
  // and the global bubble spacing setting.
  get displacementRadius(): number {
    if (this.radius === 0) {
      return 0
    }

    return this.radius + this._haloMetadata.totalWidth + this.config.bubbleParams.spacing
  }

  get totalValue(): number {
    return this._visNode.total
  }

  get absScaledTotal(): number {
    return Math.abs(this._visNode.scaledTotal)
  }

  get radius(): number {
    const globalRadiusScale = typedStore.visualisationData.bubbleRadiusScale
    const dataValueRadius = Math.abs(globalRadiusScale(Math.abs(this._visNode.scaledTotal)))
    // The Bubble's minimum size is the configured border width
    const minimumRadius = this.borderWidth
    return Math.max(minimumRadius, dataValueRadius)
  }

  get haloMetadata(): BubbleHaloMetadata {
    return this._haloMetadata
  }

  get pulseMetadata(): BubblePulseMetadata {
    return this._pulseMetadata
  }

  get fill() {
    const { display, categoryField, categoryValue } = this.config.hollowBubbles
    switch (display) {
      case HollowBubbleDisplays.NEGATIVE:
        if (this._visNode.total < 0) return HOLLOW_FILL_COLOUR
        break
      case HollowBubbleDisplays.POSITIVE:
        if (this._visNode.total > 0) return HOLLOW_FILL_COLOUR
        break
      case HollowBubbleDisplays.CATEGORY:
        if (categoryField && this._visNode.data[categoryField] === categoryValue)
          return HOLLOW_FILL_COLOUR
        break
      default:
        return this.colour
    }
    return this.colour
  }

  get borderColour() {
    return this.colour ? makeDarker(this.colour) : DEFAULT_STROKE_COLOUR
  }

  get borderWidth() {
    return Number(this.config.hollowBubbles.borderWidth)
  }

  get visNode() {
    return this._visNode
  }

  private get config() {
    return typedStore.visualisationData.bubblesConfig
  }

  private get filterLightness() {
    return this.config.filterLightness ?? 1
  }

  private get colour() {
    if (this._visNode.isTranslucent) {
      // Records that have been filtered out
      const colour = hsl(this._visNode.colour || '')
      // Adjust lightness according to user setting
      colour.l = this.filterLightness
      return colour.rgb().toString()
    }
    return this._visNode.colour ?? DEFAULT_COLOUR
  }

  onPositionChanged(callback: (coordinates: Coordinate2D) => void) {
    this._onPositionChanged = callback
  }

  updateNode(visNode: VisNode) {
    this._visNode = visNode
  }
}
